import React, { useContext, useEffect } from 'react';
import {
  CButton,
  CForm,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from '@coreui/react';
import { connect, DispatchProp } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { compact, isEmpty } from 'lodash';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Job, JobPayload } from '../../../shared/models/jobs';
import { CreateJob } from '../../actions';
import { RootState } from '../../../store/state';
import LoadingButton from '../../../shared/components/LoadingButton';
import { selectTechnicians } from '../../../store/selectors/user.selectors';
import { DEFAULT_TECHNICIAN, UserInfo } from '../../../shared/models/user';
import JobForm from '../../components/JobForm';
import { ConfigContext } from '../../../shared/contexts/journeyConfig.context';
import phoneUnification from '../../../shared/utils/validation-utils';
import { mapData } from './EditJobModal';

const validationSchema = Yup.object({
  appointment: Yup.object({
    from: Yup.date().required('Arrival Window Start is required').min(new Date(), "Arrival Window Start date cannot be less than now"),
    to: Yup.date().nullable().default(null)
      .when("from",
        (from, yup) => {
          const time = from?.getTime();
          return (time && !Number.isNaN(time)) ? yup.min(from, "Arrival Window End date cannot be before Arrival Window Start date") : yup;
        }),
    id: Yup.string(),
  }),
  description: Yup.string().required('Description is required'),
  customer: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    phones: Yup.array().of(
      Yup.string().test('phone-validation', 'Please enter a valid US or Canada number', (value) => {
        const phone = phoneUnification(value);
        return !phone || isValidPhoneNumber(phone);
      })
    ).when(['emails'], {
        is: emails => !emails || !compact(emails).length,
        then: Yup.array().of(
          Yup.string().test('phone-validation', 'Please enter a valid US or Canada number', (value) => {
            const phone = phoneUnification(value);
            return phone && isValidPhoneNumber(phone);
          })
        ).min(1, 'At least one phone number is required')
      }),
    emails: Yup.array().of(
      Yup.string().test('email-validation', 'Email is not valid', (value) => {
        return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      })
    ).when(['phones'], {
      is: phones => !phones || !compact(phones).length,
      then: Yup.array().of(
        Yup.string().test('email-validation', 'Email is not valid', (value) => {
          return value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        }).min(1, 'At least one email is required')
      )
    }),
  }, [['phones', 'emails']]),
  technician: Yup.number().required('Technician is required'),
  destination: Yup.object().shape({
    // eslint-disable-next-line react/forbid-prop-types
    lat: Yup.number().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.number().required('Lat is required'),
    }),
    lng: Yup.number().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.number().required('Lng is required'),
    }),
    address: Yup.string().when(['name'], {
      is: (name) => {
        return !name;
      },
      then: Yup.string().required('Address is required'),
    }),
    name: Yup.string().when(['address'], {
      is: address => {
        return !address;
      },
      then: Yup.string().required('Destination Name is required'),
    }),
    showCoordinates: Yup.bool(),
  }, [['lat', 'address'], ['lng', 'address'], ['address', 'name']])
});

const initialValues: JobPayload = {
  appointment: {
    from: null,
    to: null,
    id: '',
  },
  destination: {
    lat: 0,
    lng: 0,
    address: '',
    name: '',
    showCoordinates: false,
  },
  description: '',
  customer: {
    name: '',
    phones: [''],
    emails: [''],
  },
  technician: DEFAULT_TECHNICIAN,
}

export interface CreateJobProps extends DispatchProp {
  show: boolean;
  close: () => void;
  orgID: number;
  technicians: UserInfo[];
  saving: boolean;
  saved: boolean;
  onSave?: () => void;
  job?: Job;
}

const AddJobModal = (props: CreateJobProps) => {
  const { show, close, orgID, technicians, saving, saved, onSave, dispatch, job = null } = props;
  const labels = useContext(ConfigContext);
  const {
    enableOrgHierarchyModule
  } = useFlags();

  const onSubmit = (values: JobPayload) => {
    dispatch(new CreateJob({
      orgId: orgID,
      body: values,
      includeChildren: enableOrgHierarchyModule
    }));
  };

  const formik = useFormik({
      initialValues: job ? mapData(job) : initialValues,
      validationSchema,
      onSubmit,
    },
  );

  useEffect(() => {
    if (saved) {
      close();

      if (onSave) {
        onSave();
      }

      formik.resetForm();
    }
  }, [saved]);

  const handleClose = () => {
    close();
    formik.resetForm();

    if (onSave) {
      onSave();
    }
  };

  React.useEffect(() => {
    if (!isEmpty(job)) {
      formik.resetForm({ values: mapData(job) });
    }
  }, [job]);

  return (
    show && <CModal className="mt-5 pt-5" id="add-job-modal" show={ show } onClose={ handleClose } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Add {labels.task}</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            <JobForm isEdit={ false } formik={ formik } technicians={ technicians } />
          </CModalBody>
          <CModalFooter className="glympse-footer">
            <div className="glympse-btnWrapper">
              <LoadingButton color='primary' className='bg-color-orange pl-5 pr-5 border-0 roundedBtn roundBottomShadow' type='submit' isLoading={ saving }>Add {labels.task}</LoadingButton>
            </div>
            <div className="glympse-btnWrapper">
              <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ handleClose }>Cancel</CButton>
            </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  orgID: state?.orgHierarchy?.selectedOrg?._id,
  technicians: selectTechnicians(state),
  saving: state?.jobs?.updateJob?.saving,
  saved: state?.jobs?.updateJob?.saved,
});

export default connect(mapStateToProps)(AddJobModal);
